import { LoadingIndicator } from "@light-technology/components";
import { type Key } from "react";
import { useSearchParams } from "react-router";
import { type PlanResponse } from "../../state/api.types";
import { PlanComparisonFromComparisonTable } from "../PlanComparisonTable/PlanComparisonTable";
import { usePlanComparison } from "../PlanComparisonTable/common";
import { ServiceTerritorySelect } from "../PlanEditPage/ServiceTerritorySelect";
import { TermMonthsSelect } from "../PlanEditPage/TermMonthsSelect";
import { toServiceTerritory, toTermMonths } from "../PlanEditPage/common";
import { DEFAULT_SERVICE_TERRITORY } from "./common";

type PlanCompareProps = {
  plan: PlanResponse;
  planComparison: ReturnType<typeof usePlanComparison>;
};

export const PlanCompare = ({ plan, planComparison }: PlanCompareProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceTerritory = toServiceTerritory(
    searchParams.get("service_territory") ?? DEFAULT_SERVICE_TERRITORY,
  );
  const termMonths = toTermMonths(
    searchParams.get("term_months") ?? plan.term_months.toString(),
  );
  const onChangeServiceTerritory = (value: Key | null) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set("service_territory", value.toString());
    } else {
      newSearchParams.delete("service_territory");
    }
    setSearchParams(newSearchParams);
  };
  const onChangeTermMonths = (value: Key | null) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set("term_months", value.toString());
    } else {
      newSearchParams.delete("term_months");
    }
    setSearchParams(newSearchParams);
  };
  if (planComparison.isLoading) {
    return <LoadingIndicator type="brand-spinner" />;
  }

  if (!planComparison.data?.comparison_plans.length) {
    return (
      <div className="flex flex-col items-center pr-3xl pb-3xl pl-3xl">
        <div className="text-md text-primary">No competitors found</div>
        <div className="text-sm text-tertiary">
          Competitor plans for this plan will appear here.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row gap-2">
        <ServiceTerritorySelect
          value={serviceTerritory}
          onChange={onChangeServiceTerritory}
        />
        <TermMonthsSelect value={termMonths} onChange={onChangeTermMonths} />
      </div>
      <PlanComparisonFromComparisonTable
        plan={plan}
        serviceTerritory={serviceTerritory}
        planComparison={planComparison}
      />
    </div>
  );
};
